import React, { Suspense, useState } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import { Image } from '@thd-olt-component-react/image';
import { VideoPlayer } from '@thd-olt-component-react/video-player';
import { MainAsset } from '../MediaGalleryOverlay/components/MainAsset/MainAsset';
import Helper from '../../Helper';
import { mediaClick } from '../../../publisher';
import { dataModel } from '../../dataModel';

const { string } = proptypes;

const Overlay = React.lazy(() => import(/* webpackChunkName: "overlay" */ '@thd-olt-component-react/overlay')
  .then((module) => ({ default: module.Overlay })));

const IMAGE_WRAPPER_CLASSES = 'sui-w-auto sui-flex sui-justify-center [&_img]:sui-h-full sui-h-[500px]';

// Play-icon artwork, hover swap and stacking order come from the legacy stylesheet.
// The two SVGs are global THD assets, z-index 9 sits above the still image but below
// the overlay, and the 0.7 shrink applies below the $screen-sm (1024px) breakpoint.
// Written out in full: Tailwind extracts class names from the raw file text, so an
// interpolated URL would never reach the generated stylesheet. The url() must be
// unquoted: with quotes the class in the DOM never matches the one Tailwind generates,
// so the play icon renders no artwork at all.
const PLAY_ICON_CLASSES = 'sui-absolute sui-h-full sui-w-full sui-flex sui-top-0 sui-bottom-0 '
  + 'sui-left-0 sui-mx-auto sui-bg-no-repeat sui-bg-center sui-z-[9] max-lg:sui-scale-[0.7] '
  + 'sui-bg-[url(https://assets.thdstatic.com/images/v1/play-video-grey.svg)] '
  + 'hover:sui-bg-[url(https://assets.thdstatic.com/images/v1/play-video-orange.svg)]';

// 62.5% width between the $screen-xs (640px) and 767px breakpoints.
const STILL_WRAPPER_CLASSES = 'sui-relative sm:max-md:sui-w-[62.5%]';

// $dark (#333) predates the Stencil palette; sui-text-primary is #202020.
const BODY_TEXT_COLOR = 'sui-text-[#333]';

export const MediaHighlight = ({ itemId }) => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const { data, loading, error } = useDataModel('product', { variables: { itemId }, skip: !itemId });
  if ((error && !data) || loading || !data) {
    return null;
  }
  const { product } = data;
  const { identifiers, media } = product || {};
  const { images, video } = media || {};
  const { altText } = images || {};
  const brightcoveVideo = (video || []).find((v) => v.type === 'BRIGHTCOVE VIDEO');
  const lifeStyleImage = (images || []).find((i) => i.type === 'IMAGE_LIFE_STYLE'
  || i.type === 'IMAGE_LEFT_VIEW' || i.type === 'IMAGE_RIGHT_VIEW' || i.type === 'IMAGE_TOP_VIEW'
  || i.type === 'IMAGE');
  const alternateViewImage = (images || []).find((i) => i.type === 'IMAGE_ALTERNATE_VIEW');

  if (!(brightcoveVideo || lifeStyleImage || alternateViewImage)) {
    return null;
  }

  const { brandName, productLabel } = identifiers || {};
  const viewImage = lifeStyleImage || alternateViewImage;

  const mainAsset = viewImage ? Helper.normalizeImage({
    image: viewImage,
    size: '1000'
  }) : null;

  const alt = altText || `${productLabel} by ${brandName}`;

  return (
    <div data-component="MediaHighlight">
      {!brightcoveVideo
        && (
          <MainAsset
            mainAsset={mainAsset}
            zoomedAsset={mainAsset}
            onAssetClick={() => setOverlayOpen(true)}
            videoAnalyticsData={{ position: '1', section: 'media' }}
            itemId={itemId}
          />
        )}
      {brightcoveVideo
      && (
        <>
          <button
            className="[&_img]:sui-max-w-full"
            type="button"
            onClick={() => {
              setOverlayOpen(true);
              mediaClick({ section: 'highlights', component: 'media', target: 'video' });
            }}
          >
            <div className={STILL_WRAPPER_CLASSES}>
              <Image
                src={brightcoveVideo.videoStill}
                alt={brightcoveVideo.title}
                lazy
              />
              <span className={PLAY_ICON_CLASSES} />
            </div>
          </button>
          <div className={`sui-text-lg sui-font-bold sui-mt-5 ${BODY_TEXT_COLOR}`}>
            {brightcoveVideo.title}
          </div>
          <div className={`sui-text-sm sui-leading-normal sui-mt-2 ${BODY_TEXT_COLOR}`}>
            {brightcoveVideo.shortDescription}
          </div>
        </>
      )}
      {overlayOpen
      && (
        <Suspense fallback={(<div />)}>
          <Overlay closeButton onClose={() => setOverlayOpen(!overlayOpen)} open={overlayOpen}>
            <div className="sui-m-2">
              <div data-testid="media-highlight-product-label" className="sui-text-sm">
                {productLabel}
              </div>
              <div data-testid="media-highlight-brand" className="sui-text-xs">
                by{'\u00A0'}
                <span className="sui-font-bold">
                  {brandName}
                </span>
              </div>
            </div>
            {!brightcoveVideo
          && (
            <div className={IMAGE_WRAPPER_CLASSES}>
              <Image
                src={mainAsset.url}
                alt={alt}
                lazy
              />
            </div>
          )}
            { brightcoveVideo
          && (
            <div>
              <VideoPlayer
                videoId={brightcoveVideo.videoId}
                analyticsData={{ section: 'expanded-view', component: 'thumbnails', position: '1' }}
              />
            </div>
          )}
          </Overlay>
        </Suspense>
      )}
    </div>
  );

};

MediaHighlight.displayName = 'MediaHighlight';

MediaHighlight.propTypes = {
  itemId: string.isRequired
};

MediaHighlight.dataModel = dataModel;
